import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Info, Upload, Plus, Trash2 } from 'lucide-react';
import { QRStyleConfig } from './QRStyleModal';

interface QRStylePanelProps {
  config: QRStyleConfig;
  onChange: (config: QRStyleConfig) => void;
  userBranding?: {
    primaryColor?: string;
    secondaryColor?: string;
    logoUrl?: string;
  } | null;
}

interface ColorStop {
  offset: number;
  color: string;
}

const QRStylePanel: React.FC<QRStylePanelProps> = ({ config, onChange, userBranding }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['basic', 'colors']));
  const [colorMode, setColorMode] = useState<'solid' | 'gradient' | 'brand'>(() => {
    const mode = config.dotsGradient ? 'gradient' : 'solid';
    console.log('🎨 QRStylePanel - Initial color mode:', mode, 'config:', config);
    return mode;
  });
  const [logoError, setLogoError] = useState<string | null>(null);

  // Debug on mount
  React.useEffect(() => {
    console.log('🎨 QRStylePanel mounted');
    console.log('🎨 Initial expanded sections:', Array.from(expandedSections));
    console.log('🎨 Initial config:', config);
  }, []);

  const toggleSection = (section: string) => {
    console.log('🔄 Toggling section:', section);
    console.log('🔄 Current expanded sections before toggle:', Array.from(expandedSections));
    
    try {
      const newExpanded = new Set(expandedSections);
      if (newExpanded.has(section)) {
        console.log('🔄 Removing section from expanded:', section);
        newExpanded.delete(section);
      } else {
        console.log('🔄 Adding section to expanded:', section);
        newExpanded.add(section);
      }
      setExpandedSections(newExpanded);
      console.log('🔄 Expanded sections after toggle:', Array.from(newExpanded));
    } catch (error) {
      console.error('❌ Error in toggleSection:', error);
      console.error('❌ Error details:', {
        section,
        expandedSections: Array.from(expandedSections),
        error: error instanceof Error ? error.message : error
      });
    }
  };

  const updateConfig = (updates: Partial<QRStyleConfig>) => {
    console.log('🎨 QRStylePanel - Updating config with:', updates);
    const newConfig = { ...config, ...updates };
    console.log('🎨 QRStylePanel - New config:', newConfig);
    onChange(newConfig);
  };

  const handleColorModeChange = (mode: 'solid' | 'gradient' | 'brand') => {
    setColorMode(mode);
    if (mode === 'solid') {
      updateConfig({ dotsGradient: undefined, dotsColor: config.dotsColor || '#000000' });
    } else if (mode === 'gradient') {
      updateConfig({
        dotsColor: undefined,
        dotsGradient: {
          type: 'linear',
          rotation: 90,
          colorStops: [
            { offset: 0, color: '#3B82F6' },
            { offset: 1, color: '#8B5CF6' }
          ]
        }
      });
    } else if (mode === 'brand' && userBranding) {
      updateConfig({
        dotsColor: userBranding.primaryColor || '#3B82F6',
        dotsGradient: undefined
      });
    }
  };

  const updateGradientStop = (index: number, color: string) => {
    if (!config.dotsGradient) return;
    const newStops = [...config.dotsGradient.colorStops];
    newStops[index] = { ...newStops[index], color };
    updateConfig({
      dotsGradient: { ...config.dotsGradient, colorStops: newStops }
    });
  };

  const addGradientStop = () => {
    if (!config.dotsGradient) return;
    const newStops = [...config.dotsGradient.colorStops];
    const lastOffset = newStops[newStops.length - 1].offset;
    newStops.push({ offset: Math.min(lastOffset + 0.2, 1), color: '#000000' });
    updateConfig({
      dotsGradient: { ...config.dotsGradient, colorStops: newStops }
    });
  };

  const removeGradientStop = (index: number) => {
    if (!config.dotsGradient || config.dotsGradient.colorStops.length <= 2) return;
    const newStops = config.dotsGradient.colorStops.filter((_, i) => i !== index);
    updateConfig({
      dotsGradient: { ...config.dotsGradient, colorStops: newStops }
    });
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file size (max 500KB for logos)
    if (file.size > 500 * 1024) {
      setLogoError('Logo-Datei ist zu groß. Bitte verwenden Sie eine Datei unter 500KB.');
      return;
    }

    // Check file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setLogoError('Bitte verwenden Sie nur PNG, JPG oder SVG Dateien für das Logo.');
      return;
    }

    try {
      // Convert to data URL to avoid blob URL issues
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target?.result as string;
          if (result && result.startsWith('data:image/')) {
            resolve(result);
          } else {
            reject(new Error('Invalid image data'));
          }
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });

      // For large images, optionally resize them
      if (file.size > 100 * 1024 && file.type !== 'image/svg+xml') {
        // Resize image to reduce size
        const resizedDataUrl = await resizeImage(dataUrl, 200, 200);
        setLogoError(null);
        updateConfig({ logoUrl: resizedDataUrl });
      } else {
        setLogoError(null);
        updateConfig({ logoUrl: dataUrl });
      }
    } catch (error) {
      console.error('Error processing logo:', error);
      setLogoError('Fehler beim Verarbeiten des Logos. Bitte versuchen Sie es erneut.');
    }
  };

  // Helper function to resize images
  const resizeImage = async (dataUrl: string, maxWidth: number, maxHeight: number): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // Use better quality settings
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to data URL with good quality
        resolve(canvas.toDataURL('image/png', 0.9));
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = dataUrl;
    });
  };

  // Quick presets
  const presets = [
    {
      name: 'Klassisch',
      config: {
        dotsType: 'square' as const,
        dotsColor: '#000000',
        dotsGradient: undefined,
        cornersSquareType: 'square' as const,
        backgroundColor: '#FFFFFF'
      }
    },
    {
      name: 'Verlauf',
      config: {
        dotsType: 'rounded' as const,
        dotsColor: undefined,
        dotsGradient: {
          type: 'linear' as const,
          rotation: 45,
          colorStops: [
            { offset: 0, color: '#3B82F6' },
            { offset: 1, color: '#8B5CF6' }
          ]
        },
        cornersSquareType: 'extra-rounded' as const,
        backgroundColor: '#FFFFFF'
      }
    },
    {
      name: 'Abgerundet',
      config: {
        dotsType: 'dots' as const,
        dotsColor: '#10B981',
        dotsGradient: undefined,
        cornersSquareType: 'dot' as const,
        cornersDotType: 'dot' as const,
        backgroundColor: '#FFFFFF'
      }
    },
    {
      name: 'Elegant',
      config: {
        dotsType: 'classy-rounded' as const,
        dotsColor: '#1F2937',
        dotsGradient: undefined,
        cornersSquareType: 'extra-rounded' as const,
        backgroundColor: '#F9FAFB',
        frameStyle: 'rounded' as const,
        frameColor: '#1F2937'
      }
    }
  ];

  return (
    <div className="space-y-4">
      {/* Quick Presets */}
      <div className="mb-4">
        <h3 className="font-semibold mb-2">Schnellvorlagen</h3>
        <div className="grid grid-cols-2 gap-2">
          {presets.map((preset) => (
            <label
              key={preset.name}
              className="flex items-center p-2 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              <input
                type="radio"
                name="preset"
                className="mr-2"
                onChange={() => {
                  updateConfig(preset.config);
                  // Set color mode based on preset
                  if (preset.config.dotsGradient) {
                    setColorMode('gradient');
                  } else if (preset.config.dotsColor) {
                    setColorMode('solid');
                  }
                }}
              />
              <span className="text-sm font-medium">{preset.name}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Basic Settings */}
      <div className="border rounded-lg">
        <button
          type="button"
          onClick={() => toggleSection('basic')}
          className="w-full px-4 py-3 flex justify-between items-center hover:bg-gray-50"
        >
          <span className="font-medium">Grundeinstellungen</span>
          {expandedSections.has('basic') ? <ChevronUp /> : <ChevronDown />}
        </button>
        {expandedSections.has('basic') && (
          <div className="px-4 pb-4 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Fehlerkorrektur-Level
                <button type="button" className="ml-2 text-gray-400 hover:text-gray-600">
                  <Info className="w-4 h-4 inline" />
                </button>
              </label>
              <select
                value={config.errorCorrectionLevel}
                onChange={(e) => updateConfig({ errorCorrectionLevel: e.target.value as any })}
                className="w-full p-2 border rounded-lg"
              >
                <option value="L">L - Niedrig (7%)</option>
                <option value="M">M - Mittel (15%)</option>
                <option value="Q">Q - Hoch (25%)</option>
                <option value="H">H - Sehr hoch (30%)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Höhere Level erlauben mehr Beschädigung, erhöhen aber die Komplexität
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Colors & Patterns */}
      <div className="border rounded-lg">
        <button
          type="button"
          onClick={() => toggleSection('colors')}
          className="w-full px-4 py-3 flex justify-between items-center hover:bg-gray-50"
        >
          <span className="font-medium">Farben & Muster</span>
          {expandedSections.has('colors') ? <ChevronUp /> : <ChevronDown />}
        </button>
        {expandedSections.has('colors') && (
          <div className="px-4 pb-4 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Musterstil</label>
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { value: 'square', label: 'Quadratisch' },
                    { value: 'dots', label: 'Punkte' },
                    { value: 'rounded', label: 'Abgerundet' },
                    { value: 'classy', label: 'Klassisch' },
                    { value: 'classy-rounded', label: 'Klassisch Rund' },
                    { value: 'extra-rounded', label: 'Extra Rund' }
                  ].map((type) => (
                    <label key={type.value} className="flex items-center">
                      <input
                        type="radio"
                        name="pattern_style"
                        value={type.value}
                        checked={config.dotsType === type.value}
                        onChange={() => updateConfig({ dotsType: type.value as any })}
                        className="mr-2 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Farbmodus</label>
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg mb-3">
                <div className="grid grid-cols-2 gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="color_mode"
                      value="solid"
                      checked={colorMode === 'solid'}
                      onChange={() => handleColorModeChange('solid')}
                      className="mr-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Einfarbig</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="color_mode"
                      value="gradient"
                      checked={colorMode === 'gradient'}
                      onChange={() => handleColorModeChange('gradient')}
                      className="mr-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Verlauf</span>
                  </label>
                  {userBranding && (
                    <label className="flex items-center col-span-2">
                      <input
                        type="radio"
                        name="color_mode"
                        value="brand"
                        checked={colorMode === 'brand'}
                        onChange={() => handleColorModeChange('brand')}
                        className="mr-2 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Markenfarben</span>
                    </label>
                  )}
                </div>
              </div>

              {colorMode === 'solid' && (
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={config.dotsColor || '#000000'}
                    onChange={(e) => updateConfig({ dotsColor: e.target.value })}
                    className="w-20 h-10 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={config.dotsColor || '#000000'}
                    onChange={(e) => updateConfig({ dotsColor: e.target.value })}
                    className="flex-1 p-2 border rounded-lg"
                  />
                </div>
              )}

              {colorMode === 'gradient' && config.dotsGradient && (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <select
                      value={config.dotsGradient.type}
                      onChange={(e) => updateConfig({
                        dotsGradient: { ...config.dotsGradient!, type: e.target.value as any }
                      })}
                      className="flex-1 p-2 border rounded-lg"
                    >
                      <option value="linear">Linear</option>
                      <option value="radial">Radial</option>
                    </select>
                    {config.dotsGradient.type === 'linear' && (
                      <input
                        type="range"
                        min="0"
                        max="360"
                        value={config.dotsGradient.rotation || 90}
                        onChange={(e) => updateConfig({
                          dotsGradient: { ...config.dotsGradient!, rotation: parseInt(e.target.value) }
                        })}
                        className="flex-1"
                      />
                    )}
                  </div>
                  <div className="space-y-2">
                    {config.dotsGradient.colorStops.map((stop, index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <input
                          type="color"
                          value={stop.color}
                          onChange={(e) => updateGradientStop(index, e.target.value)}
                          className="w-12 h-8 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={stop.color}
                          onChange={(e) => updateGradientStop(index, e.target.value)}
                          className="flex-1 p-1 border rounded text-sm"
                        />
                        {config.dotsGradient!.colorStops.length > 2 && (
                          <button
                            type="button"
                            onClick={() => removeGradientStop(index)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addGradientStop}
                      className="text-sm text-blue-500 hover:text-blue-600 flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      Farbe hinzufügen
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Eckenstil</label>
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg mb-3">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { value: 'square', label: 'Quadratisch' },
                    { value: 'dot', label: 'Punkt' },
                    { value: 'extra-rounded', label: 'Extra Rund' }
                  ].map((type) => (
                    <label key={type.value} className="flex items-center">
                      <input
                        type="radio"
                        name="corner_style"
                        value={type.value}
                        checked={config.cornersSquareType === type.value}
                        onChange={() => updateConfig({ 
                          cornersSquareType: type.value as any,
                          cornersDotType: type.value === 'extra-rounded' ? 'square' : type.value as any 
                        })}
                        className="mr-2 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  value={config.cornersColor || '#000000'}
                  onChange={(e) => updateConfig({ cornersColor: e.target.value })}
                  className="w-20 h-10 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={config.cornersColor || '#000000'}
                  onChange={(e) => updateConfig({ cornersColor: e.target.value })}
                  className="flex-1 p-2 border rounded-lg"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Hintergrund</label>
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  id="transparent-bg"
                  checked={config.backgroundTransparent}
                  onChange={(e) => updateConfig({ backgroundTransparent: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="transparent-bg" className="text-sm">Transparent</label>
              </div>
              {!config.backgroundTransparent && (
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={config.backgroundColor || '#FFFFFF'}
                    onChange={(e) => updateConfig({ backgroundColor: e.target.value })}
                    className="w-20 h-10 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={config.backgroundColor || '#FFFFFF'}
                    onChange={(e) => updateConfig({ backgroundColor: e.target.value })}
                    className="flex-1 p-2 border rounded-lg"
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Logo */}
      <div className="border rounded-lg">
        <button
          type="button"
          onClick={() => toggleSection('logo')}
          className="w-full px-4 py-3 flex justify-between items-center hover:bg-gray-50"
        >
          <span className="font-medium">Logo</span>
          {expandedSections.has('logo') ? <ChevronUp /> : <ChevronDown />}
        </button>
        {expandedSections.has('logo') && (
          <div className="px-4 pb-4 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Logo-Bild</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                {config.logoUrl ? (
                  <div className="space-y-2">
                    <img 
                      src={config.logoUrl} 
                      alt="Logo preview" 
                      className="w-20 h-20 mx-auto object-contain"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setLogoError(null);
                        updateConfig({ logoUrl: undefined });
                      }}
                      className="text-sm text-red-500 hover:text-red-600"
                    >
                      Logo entfernen
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <label className="cursor-pointer">
                      <span className="text-sm text-blue-500 hover:text-blue-600">
                        Logo hochladen
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                    </label>
                    {userBranding?.logoUrl && (
                      <button
                        type="button"
                        onClick={() => {
                          setLogoError(null);
                          updateConfig({ logoUrl: userBranding.logoUrl });
                        }}
                        className="block w-full mt-2 text-sm text-gray-600 hover:text-gray-800"
                      >
                        Markenlogo verwenden
                      </button>
                    )}
                  </>
                )}
              </div>
              {logoError && (
                <p className="mt-2 text-sm text-red-600">{logoError}</p>
              )}
            </div>

            {config.logoUrl && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Logo-Größe: {config.logoSize}%
                  </label>
                  <input
                    type="range"
                    min="10"
                    max="40"
                    value={config.logoSize || 25}
                    onChange={(e) => updateConfig({ logoSize: parseInt(e.target.value) })}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Logo-Abstand: {config.logoMargin}px
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="20"
                    value={config.logoMargin || 6}
                    onChange={(e) => updateConfig({ logoMargin: parseInt(e.target.value) })}
                    className="w-full"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="hide-dots"
                    checked={config.hideBackgroundDots}
                    onChange={(e) => updateConfig({ hideBackgroundDots: e.target.checked })}
                    className="rounded"
                  />
                  <label htmlFor="hide-dots" className="text-sm">
                    Punkte hinter Logo verstecken
                  </label>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Frame & Label */}
      <div 
        className="border rounded-lg"
        onClick={(e) => {
          console.log('🔍 Frame container div clicked');
          e.stopPropagation();
        }}
      >
        <div
          role="button"
          tabIndex={0}
          onClick={(e) => {
            console.log('🖱️ Frame div-button clicked');
            console.log('🖱️ Event type:', e.type);
            console.log('🖱️ Event target:', e.target);
            console.log('🖱️ Event currentTarget:', e.currentTarget);
            e.preventDefault();
            e.stopPropagation();
            toggleSection('frame');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              console.log('🖱️ Frame keyboard activation');
              e.preventDefault();
              e.stopPropagation();
              toggleSection('frame');
            }
          }}
          className="w-full px-4 py-3 flex justify-between items-center hover:bg-gray-50 cursor-pointer"
        >
          <span className="font-medium">Rahmen & Beschriftung</span>
          {expandedSections.has('frame') ? <ChevronUp /> : <ChevronDown />}
        </div>
        {expandedSections.has('frame') && (
          <div className="px-4 pb-4 space-y-4">
            {console.log('🔍 Rahmen & Beschriftung section expanded')}
            {console.log('🔍 Current config when frame expanded:', config)}
            <div>
              <label className="block text-sm font-medium mb-2">Rahmenstil</label>
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { value: 'none', label: 'Kein Rahmen' },
                    { value: 'square', label: 'Quadratisch' },
                    { value: 'rounded', label: 'Abgerundet' },
                    { value: 'circle', label: 'Kreis' }
                  ].map((style) => (
                    <label key={style.value} className="flex items-center">
                      <input
                        type="radio"
                        name="frame_style"
                        value={style.value}
                        checked={config.frameStyle === style.value}
                        onChange={() => updateConfig({ frameStyle: style.value as any })}
                        className="mr-2 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{style.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {config.frameStyle !== 'none' && (
              <>
                {console.log('🔍 Rendering frame options, frameStyle:', config.frameStyle)}
                {console.log('🔍 Frame config:', { frameSize: config.frameSize, frameColor: config.frameColor, frameLabel: config.frameLabel })}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Rahmenstärke: {config.frameSize}px
                  </label>
                  <input
                    type="range"
                    min="4"
                    max="32"
                    value={config.frameSize || 16}
                    onChange={(e) => updateConfig({ frameSize: parseInt(e.target.value) })}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Rahmenfarbe</label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="color"
                      value={config.frameColor || '#000000'}
                      onChange={(e) => updateConfig({ frameColor: e.target.value })}
                      className="w-20 h-10 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={config.frameColor || '#000000'}
                      onChange={(e) => updateConfig({ frameColor: e.target.value })}
                      className="flex-1 p-2 border rounded-lg"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Rahmenbeschriftung</label>
                  <input
                    type="text"
                    placeholder="Hier scannen!"
                    value={config.frameLabel || ''}
                    onChange={(e) => updateConfig({ frameLabel: e.target.value })}
                    className="w-full p-2 border rounded-lg"
                  />
                </div>

                {config.frameLabel && (
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Schriftgröße: {config.frameLabelSize}px
                    </label>
                    <input
                      type="range"
                      min="12"
                      max="48"
                      value={config.frameLabelSize || 20}
                      onChange={(e) => updateConfig({ frameLabelSize: parseInt(e.target.value) })}
                      className="w-full"
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QRStylePanel;